import React from 'react';

/**
 * Recovers from the "failed to load a lazily-imported chunk" error that happens
 * when a user has the SPA open in a tab, a new version deploys, and they then
 * navigate to a lazy route whose old chunk is no longer reachable. index.html is
 * served `no-cache`, so a single reload pulls the current index.html → current
 * chunk names and the navigation succeeds.
 *
 * Design notes:
 * - We do NOT try to pattern-match the exact error message. Browsers/bundlers
 *   word chunk-load failures inconsistently (and an over-narrow regex was why an
 *   earlier version still white-screened). This boundary wraps the whole router,
 *   so essentially the only errors that reach it are fatal render errors that
 *   would otherwise blank the page anyway — for those, a one-shot reload is
 *   strictly better than a white screen.
 * - A timestamp guard (not a one-time flag) prevents reload loops while still
 *   allowing recovery from a *later*, unrelated stale-chunk error in a long
 *   session: we only auto-reload if we haven't already reloaded in the last few
 *   seconds. If the error survives that reload, we show a manual fallback.
 * - Donor-facing routes are eager (not lazy), so this only ever triggers for
 *   logged-in users navigating the app — never on the public donation flow.
 */
const RELOAD_TS = 'chunk-reload-ts';
const RELOAD_WINDOW_MS = 8000;

interface State {
  error: Error | null;
}

class ChunkErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch() {
    let last = 0;
    try { last = Number(sessionStorage.getItem(RELOAD_TS) || '0'); } catch { /* private mode */ }
    const now = Date.now();
    // If we haven't just reloaded, this is almost certainly a stale chunk after
    // a deploy — reload once to pull the fresh build. If we reloaded moments ago
    // and it's STILL failing, fall through to the manual fallback (no loop).
    if (now - last > RELOAD_WINDOW_MS) {
      try { sessionStorage.setItem(RELOAD_TS, String(now)); } catch { /* private mode */ }
      window.location.reload();
    }
  }

  render() {
    if (this.state.error) {
      // Never rethrow to a blank page. Either a reload is already in flight (from
      // componentDidCatch) and this fallback flashes for a frame, or the reload
      // didn't fix it and the user gets an explicit retry.
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-4 text-center p-6">
          <p className="text-gray-700">המערכת עודכנה. נטען מחדש כדי לקבל את הגרסה החדשה…</p>
          <button
            className="px-4 py-2 rounded bg-blue-600 text-white"
            onClick={() => {
              try { sessionStorage.removeItem(RELOAD_TS); } catch { /* private mode */ }
              window.location.reload();
            }}
          >
            רענון
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
